import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export interface BatchFileEntry {
  fileName: string;
  fileSize: number;
  status: 'queued' | 'processing' | 'done' | 'duplicate' | 'error';
  quoteId?: string;
  itemId?: string;
  vendorName?: string;
  categoryName?: string;
  error?: string;
  contractId?: string;
  /** When status is 'duplicate', the contract that the upload conflicted with. */
  duplicateOfContractId?: string;
  duplicateOfItemId?: string;
  duplicateOfVendorName?: string;
}

interface BatchUploadState {
  isActive: boolean;
  isMinimized: boolean;
  projectId: string | null;
  batchId: string | null;
  files: BatchFileEntry[];
  uploadType: 'quotes' | 'contracts';
}

interface BatchUploadContextValue {
  state: BatchUploadState;
  startBatch: (projectId: string, files: File[], uploadType?: 'quotes' | 'contracts') => void;
  dismiss: () => void;
  toggleMinimize: () => void;
  retryFile: (index: number) => void;
}

const BatchUploadContext = createContext<BatchUploadContextValue | null>(null);

export function useBatchUpload() {
  const ctx = useContext(BatchUploadContext);
  if (!ctx) throw new Error('useBatchUpload must be used within BatchUploadProvider');
  return ctx;
}

export function BatchUploadProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<BatchUploadState>({
    isActive: false,
    isMinimized: false,
    projectId: null,
    batchId: null,
    files: [],
    uploadType: 'quotes',
  });
  // Each call to startBatch() bumps this token. In-flight workers from a prior
  // batch compare their captured token against tokenRef.current and bail out
  // before writing into state, so they cannot corrupt the new batch's rows.
  const tokenRef = useRef(0);

  const uploadTypeRef = useRef<'quotes' | 'contracts'>('quotes');

  const CONCURRENCY = 4;

  const processOne = useCallback(async (projectId: string, batchId: string, file: File, index: number, token: number) => {
    const uploadType = uploadTypeRef.current;
    const endpoint = uploadType === 'contracts'
      ? '/api/contract-import/parse-and-create'
      : '/api/quote-import/parse-and-create';

    if (tokenRef.current !== token) return;
    setState((prev) => ({
      ...prev,
      files: prev.files.map((f, idx) =>
        idx === index ? { ...f, status: 'processing' as const } : f
      ),
    }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', projectId);
      formData.append('batchId', batchId);

      const res = await fetch(endpoint, {
        method: 'POST',
        body: formData,
      });

      if (res.status === 409) {
        const err = await res.json().catch(() => ({}));
        if (err.duplicate) {
          if (tokenRef.current !== token) return;
          setState((prev) => ({
            ...prev,
            files: prev.files.map((f, idx) =>
              idx === index
                ? {
                    ...f,
                    status: 'duplicate' as const,
                    duplicateOfContractId: err.existingContractId ?? undefined,
                    duplicateOfItemId: err.existingItemId ?? undefined,
                    duplicateOfVendorName: err.existingVendorName ?? undefined,
                  }
                : f
            ),
          }));
          return;
        }
      }

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to process');
      }

      const data = await res.json();

      if (tokenRef.current !== token) return;
      if (uploadType === 'contracts') {
        setState((prev) => ({
          ...prev,
          files: prev.files.map((f, idx) =>
            idx === index
              ? {
                  ...f,
                  status: 'done' as const,
                  contractId: data.contractId,
                  itemId: data.itemId,
                  vendorName: data.vendorName,
                }
              : f
          ),
        }));
      } else {
        setState((prev) => ({
          ...prev,
          files: prev.files.map((f, idx) =>
            idx === index
              ? {
                  ...f,
                  status: 'done' as const,
                  quoteId: data.quoteId,
                  itemId: data.itemId,
                  vendorName: data.vendorName,
                  categoryName: data.categoryName,
                }
              : f
          ),
        }));
      }

      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      queryClient.invalidateQueries({ queryKey: ['project-vendors', projectId] });
      queryClient.invalidateQueries({ queryKey: ['vendor-category-suggestions'] });
      // Item modals may be open or visited after the upload; invalidate any
      // cached item detail so re-opening shows the freshly-attached contract.
      queryClient.invalidateQueries({ queryKey: ['item'] });
    } catch (err) {
      if (tokenRef.current !== token) return;
      setState((prev) => ({
        ...prev,
        files: prev.files.map((f, idx) =>
          idx === index
            ? { ...f, status: 'error' as const, error: err instanceof Error ? err.message : 'Unknown error' }
            : f
        ),
      }));
    }
  }, [queryClient]);

  const processFiles = useCallback(async (projectId: string, batchId: string, files: File[], token: number) => {
    let cursor = 0;
    const workerCount = Math.min(CONCURRENCY, files.length);
    const workers = Array.from({ length: workerCount }, async () => {
      while (tokenRef.current === token) {
        const i = cursor++;
        if (i >= files.length) return;
        await processOne(projectId, batchId, files[i]!, i, token);
      }
    });
    await Promise.all(workers);
  }, [processOne]);

  // Store refs for retry (avoids stale closures)
  const filesRef = useRef<File[]>([]);
  const projectIdRef = useRef<string | null>(null);
  const batchIdRef = useRef<string | null>(null);

  const startBatch = useCallback((projectId: string, files: File[], uploadType: 'quotes' | 'contracts' = 'quotes') => {
    const batchId = crypto.randomUUID();
    const token = tokenRef.current + 1;
    tokenRef.current = token;
    filesRef.current = files;
    projectIdRef.current = projectId;
    batchIdRef.current = batchId;
    uploadTypeRef.current = uploadType;

    const entries: BatchFileEntry[] = files.map((f) => ({
      fileName: f.name,
      fileSize: f.size,
      status: 'queued' as const,
    }));

    setState({
      isActive: true,
      isMinimized: false,
      projectId,
      batchId,
      files: entries,
      uploadType,
    });

    processFiles(projectId, batchId, files, token);
  }, [processFiles]);

  const dismiss = useCallback(() => {
    // Bumping the token voids any in-flight workers from this or earlier batches.
    tokenRef.current += 1;
    setState({
      isActive: false,
      isMinimized: false,
      projectId: null,
      batchId: null,
      files: [],
      uploadType: 'quotes',
    });
  }, []);

  const toggleMinimize = useCallback(() => {
    setState((prev) => ({ ...prev, isMinimized: !prev.isMinimized }));
  }, []);

  const retryFile = useCallback((index: number) => {
    const currentProjectId = projectIdRef.current;
    const currentBatchId = batchIdRef.current;
    if (!currentProjectId || !currentBatchId) return;
    const files = filesRef.current;
    if (!files[index]) return;

    setState((prev) => ({
      ...prev,
      files: prev.files.map((f, idx) =>
        idx === index ? { ...f, status: 'queued' as const, error: undefined } : f
      ),
    }));

    processOne(currentProjectId, currentBatchId, files[index]!, index, tokenRef.current);
  }, [processOne]);

  return (
    <BatchUploadContext.Provider value={{ state, startBatch, dismiss, toggleMinimize, retryFile }}>
      {children}
    </BatchUploadContext.Provider>
  );
}
